import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";

import {
  chartHint,
  denseReadout,
  nextSelectable,
  pairedReadout,
  singleReadout,
  slotAt,
  type ChartKey,
  type ChartPer,
} from "../domain/chartView";

export interface PairedDay {
  key: string;
  /** the axis label under the pair: "Thu" */
  label: string;
  /** the full day or week the readout names: "Tue 15 Sep", "Week of 14 Sep" */
  name?: string;
  /** null: nothing logged — no bar, not a zero one */
  in: number | null;
  out: number | null;
  /** today: its label is ink and bold */
  current?: boolean;
}

export interface SingleBar {
  key: string;
  label: string;
  /** the full day or week the readout names: "Tue 15 Sep", "Week of 14 Sep" */
  name?: string;
  value: number | null;
  /** the brand bar with its value above; the others are past (rest) */
  current?: boolean;
  /** overrides the current/past colour: a week where every day is brand but the low ones */
  tone?: "brand" | "rest";
}

interface Common {
  ariaLabel: string;
  empty?: ReactNode;
  /** a bar was tapped or reached by key (its `key`; dense: the hour), or the selection cleared */
  onSelect?: (key: string | null) => void;
}

type Props =
  | (Common & {
      /** In (outline, --c-in) beside Out (solid, --c-out), per day — SNIPPETS §19, 184 tall */
      mode: "paired";
      days: PairedDay[];
      /** a pair a day (default) or a pair a week — the hint's word and the readout's "avg … a day" */
      per?: "day" | "week";
      /** the average In, drawn as a dashed "avg" rule */
      avg?: number | null;
      /** Out is estimated: its value label gets "~" */
      outEstimated?: boolean;
      /** "in" / "out" under the first pair (default true). Off past a week of pairs, where a
       *  narrowed pair cannot hold them, or with no In series — the page then draws a legend row. */
      words?: boolean;
    })
  | (Common & {
      /** one series by day, current brand, past rest — the 7-day variant of §20, 120 tall */
      mode: "single";
      bars: SingleBar[];
      /** a bar a day (default) or a bar a week — the hint's word */
      per?: "day" | "week";
      avg?: number | null;
      /** a reference line in ink, labelled with its own word ("WHO", "goal") instead of "avg" */
      rule?: { value: number; label: string };
      /** the value printed over the current bar; a rounded, grouped number by default */
      format?: (v: number) => string;
      /** the readout's word after the figure: "steps" */
      unit?: string;
      /** the page's own readout for a selected bar: "Tue 15 Sep · 6,214 steps · of 8,000 goal" */
      readout?: (bar: SingleBar) => string;
    })
  | (Common & {
      /** 24 hourly cells — SNIPPETS §20, 120 tall. Empty hours draw nothing. */
      mode: "dense";
      values: (number | null)[];
      /** the hour drawn brand with its value above; the others are past (rest) */
      current?: number;
      /** the readout's word after the figure: "steps" */
      unit?: string;
    });

export type BarChartProps = Props;

const fmt = (n: number) => Math.round(n).toLocaleString();

function useWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => setW(el.clientWidth);
    set();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w || 358] as const;
}

/** The bars' keys in order: a dense chart's are its hours. */
function keysOf(p: Props): string[] {
  if (p.mode === "paired") return p.days.map((d) => d.key);
  if (p.mode === "single") return p.bars.map((b) => b.key);
  return p.values.map((_, i) => String(i));
}

/** Which bars have something to read — the arrow keys skip the rest. */
export function presentBars(p: Props): boolean[] {
  if (p.mode === "paired") return p.days.map((d) => (d.in ?? 0) > 0 || (d.out ?? 0) > 0);
  if (p.mode === "single") return p.bars.map((b) => (b.value ?? 0) > 0);
  return p.values.map((v) => (v ?? 0) > 0);
}

/** Where the slots run, in the chart's own units — the tap's arithmetic. */
function slots(p: Props, W: number): { left: number; slot: number; n: number } {
  if (p.mode === "paired") {
    const n = p.days.length;
    return { left: 30, slot: (W - 52 - 30) / Math.max(1, n), n };
  }
  if (p.mode === "single") {
    const L = p.rule ? 36 : p.avg ? 30 : 0;
    const n = p.bars.length;
    return { left: L, slot: (W - L) / Math.max(1, n), n };
  }
  const n = p.values.length || 24;
  return { left: 0, slot: W / n, n };
}

/** The readout line's words: the hint, or the selected bar's day and figure. */
export function chartReadout(p: Props, sel: number | null): string {
  if (p.mode === "paired") {
    const per: ChartPer = p.per ?? "day";
    const d = sel === null ? undefined : p.days[sel];
    if (!d) return chartHint(per);
    return pairedReadout(d.name ?? (d.label || d.key), d.in, d.out, { outEstimated: p.outEstimated, weekly: per === "week" });
  }
  if (p.mode === "single") {
    const b = sel === null ? undefined : p.bars[sel];
    if (!b) return chartHint(p.per ?? "day");
    if (p.readout && b.value !== null && b.value > 0) return p.readout(b);
    return singleReadout(b.name ?? (b.label || b.key), b.value, p.format ?? fmt, p.unit);
  }
  if (sel === null || sel < 0 || sel >= p.values.length) return chartHint("hour");
  return denseReadout(sel, p.values[sel], p.unit);
}

/** The drawing alone, with `sel` the selected bar's index — pure, so a test can render it. */
export function chartSvg(p: Props, W: number, sel: number | null): ReactNode {
  if (p.mode === "paired") return paired(p, W, sel);
  if (p.mode === "single") return single(p, W, sel);
  return dense(p, W, sel);
}

/**
 * Anything summed per day or hour, in one chart language (SNIPPETS §19–20): bars by date, a
 * direct value on the last (or current) bar, a dashed rule labelled "avg", axis text .8rem, no
 * legend — "in" and "out" are written under the first pair instead (`words={false}` drops them
 * when the page draws its own legend row).
 *
 * `single` can swap "avg" for a `rule` with its own word (the WHO line, the step goal), colour
 * each bar by `tone`, and `format` the current bar's value ("3 h 10").
 *
 * Every bar can be read (ADR-061): a tap selects the slot under it (the same one again, or a tap
 * past either end, clears), the arrow keys move between bars with something to read, Home/End
 * jump, Escape clears. The selected bar is ink with its figure above it, the current bar's own
 * figure steps aside, and the readout line under the chart names the day and the exact figure —
 * the hint while nothing is selected, so the line is always there and the layout never jumps.
 *
 * When to use: `paired` for In vs Out (Food and Progress energy), `single` for a week of one
 * series (steps, burn), `dense` for steps by hour on /today/activity.
 */
export function BarChart(props: Props) {
  const [ref, W] = useWidth();
  const [selKey, setSelKey] = useState<string | null>(null);

  const keys = keysOf(props);
  const at = selKey === null ? -1 : keys.indexOf(selKey);
  // A key the data no longer holds (the window changed under it) is no selection.
  const sel = at >= 0 ? at : null;
  const present = presentBars(props);
  const hasData = present.some(Boolean);

  const choose = (i: number | null) => {
    const key = i === null ? null : (keys[i] ?? null);
    setSelKey(key);
    props.onSelect?.(key);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    // The plot is the svg's box; its viewBox is W wide, so a tap scales into the chart's units.
    const box = (e.currentTarget.querySelector("svg") ?? e.currentTarget).getBoundingClientRect();
    if (!box.width) return;
    const x = ((e.clientX - box.left) * W) / box.width;
    const g = slots(props, W);
    const i = slotAt(x, g.left, g.slot, g.n);
    choose(i === null || i === sel ? null : i);
  };

  const MOVES: Record<string, ChartKey> = { ArrowRight: "next", ArrowLeft: "prev", Home: "first", End: "last" };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && sel !== null) {
      e.preventDefault();
      choose(null);
      return;
    }
    const move = MOVES[e.key];
    if (!move) return;
    e.preventDefault();
    choose(nextSelectable(present, sel, move));
  };

  if (!hasData)
    return (
      <div ref={ref} className="k-chart">
        <p className="k-chart__empty">{props.empty ?? "Nothing to show yet."}</p>
      </div>
    );

  return (
    <div
      ref={ref}
      className="k-chart k-chart--live"
      tabIndex={0}
      role="group"
      aria-label={`${props.ariaLabel}; the arrow keys read each bar`}
      onKeyDown={onKeyDown}
    >
      <div className="k-chart__plot" onPointerDown={onPointerDown}>
        {chartSvg(props, W, sel)}
      </div>
      <p className={`k-chart__readout${sel !== null ? " is-sel" : ""}`} aria-live="polite">
        {chartReadout(props, sel)}
      </p>
    </div>
  );
}

/** A figure centred over `cx`, or hung from the nearer edge when centring would run off it. */
function placed(cx: number, text: string, W: number): { x: number; anchor: "start" | "middle" | "end" } {
  const half = (text.length * 7.5) / 2;
  if (cx + half > W) return { x: W, anchor: "end" };
  if (cx - half < 0) return { x: 0, anchor: "start" };
  return { x: cx, anchor: "middle" };
}

/** Invisible rects over each whole slot: the tap target is the plot's full height, not the bar. */
function hits(n: number, left: number, slot: number, H: number) {
  return Array.from({ length: n }, (_, i) => (
    <rect key={`hit${i}`} className="g-hit" x={left + i * slot} y={0} width={slot} height={H} fill="transparent" />
  ));
}

function paired(p: Extract<Props, { mode: "paired" }>, W: number, sel: number | null) {
  const words = p.words !== false;
  const H = words ? 184 : 166;
  const L = 30;
  const R = W - 52;
  const base = 140;
  const top = 24;
  const n = p.days.length;
  const slot = (R - L) / Math.max(1, n);
  const max = Math.max(100, ...p.days.map((d) => Math.max(d.in ?? 0, d.out ?? 0)), p.avg ?? 0);
  const h = (v: number) => (v / max) * (base - top);
  // 15-wide bars 3 apart fit seven days; fourteen have to narrow, or each pair overprints the next.
  const bw = Math.max(3, Math.min(15, (slot - 7) / 2));
  const pair = bw * 2 + 3;
  const x0 = (i: number) => L + i * slot + (slot - pair) / 2;
  const last = p.days[n - 1];

  // The last pair's values at the right edge, kept at least a label apart.
  let outY = last?.out != null ? base - h(last.out) + 5 : null;
  let inY = last?.in != null ? base - h(last.in) + 5 : null;
  if (outY !== null && inY !== null && Math.abs(outY - inY) < 14) {
    if (outY <= inY) inY = outY + 14;
    else outY = inY + 14;
  }

  // The selected pair's figures stacked over it, In above Out, a label apart.
  const s = sel === null ? undefined : p.days[sel];
  const selFigs: { text: string }[] = [];
  if (s) {
    if (s.in != null && s.in > 0) selFigs.push({ text: fmt(s.in) });
    if (s.out != null && s.out > 0) selFigs.push({ text: `${p.outEstimated ? "~" : ""}${fmt(s.out)}` });
  }
  const selTop = s ? Math.max(12, base - h(Math.max(s.in ?? 0, s.out ?? 0)) - 6 - 14 * (selFigs.length - 1)) : 0;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={p.ariaLabel}>
      {hits(n, L, slot, H)}
      {p.days.map((d, i) => (
        <g key={d.key}>
          {d.in != null && d.in > 0 ? (
            <rect
              className={i === sel ? "b-in b-sel" : "b-in"}
              x={x0(i)}
              y={base - h(d.in)}
              width={bw}
              height={h(d.in)}
              rx="2"
            />
          ) : null}
          {d.out != null && d.out > 0 ? (
            <rect
              className={i === sel ? "b-out b-sel" : "b-out"}
              x={x0(i) + bw + 3}
              y={base - h(d.out)}
              width={bw}
              height={h(d.out)}
              rx="2"
            />
          ) : null}
          <text className={d.current ? "t-ink" : undefined} x={x0(i) + pair / 2} y={158} textAnchor="middle">
            {d.label}
          </text>
          {i === 0 && words ? (
            <>
              {/* the words are wider than a narrowed pair, so they sit side by side from its left edge */}
              <text className="t-in" x={bw >= 15 ? x0(i) + 7.5 : x0(i)} y={176} textAnchor={bw >= 15 ? "middle" : "start"}>
                in
              </text>
              <text
                className="t-brand"
                x={bw >= 15 ? x0(i) + 25.5 : x0(i) + 18}
                y={176}
                textAnchor={bw >= 15 ? "middle" : "start"}
              >
                out
              </text>
            </>
          ) : null}
        </g>
      ))}
      {p.avg ? (
        <>
          <line className="g-avg" x1={L} y1={base - h(p.avg)} x2={R} y2={base - h(p.avg)} />
          <text className="t-in" x={0} y={base - h(p.avg) + 4.5}>
            avg
          </text>
        </>
      ) : null}
      {/* Figures are drawn last, over the bars and the rule, so their halo keeps them legible.
          Two figures over neighbouring bars collide, so the last pair's step aside for a selection. */}
      {sel === null && outY !== null && last?.out != null ? (
        <text className="t-brand t-halo" x={R + 4} y={outY}>
          {p.outEstimated ? "~" : ""}
          {fmt(last.out)}
        </text>
      ) : null}
      {sel === null && inY !== null && last?.in != null ? (
        <text className="t-in t-halo" x={R + 4} y={inY}>
          {fmt(last.in)}
        </text>
      ) : null}
      {s
        ? selFigs.map((f, k) => {
            const at = placed(x0(sel ?? 0) + pair / 2, f.text, W);
            return (
              <text key={`sel${k}`} className="t-ink t-sel t-halo" x={at.x} y={selTop + 14 * k} textAnchor={at.anchor}>
                {f.text}
              </text>
            );
          })
        : null}
      <line className="g-grid" x1={L} y1={base + 0.5} x2={R} y2={base + 0.5} />
    </svg>
  );
}

function single(p: Extract<Props, { mode: "single" }>, W: number, sel: number | null) {
  const H = 120;
  const L = p.rule ? 36 : p.avg ? 30 : 0;
  const base = 92;
  const top = 21;
  const n = p.bars.length;
  const slot = (W - L) / Math.max(1, n);
  const bw = Math.min(15, slot - 4);
  const max = Math.max(1, ...p.bars.map((b) => b.value ?? 0), p.avg ?? 0, p.rule?.value ?? 0);
  const h = (v: number) => Math.max(1.2, (v / max) * (base - top));
  const cx = (i: number) => L + i * slot + slot / 2;
  const show = p.format ?? fmt;
  const barClass = (b: SingleBar, i: number) =>
    i === sel ? "b-sel" : (b.tone ?? (b.current ? "brand" : "rest")) === "brand" ? "b-cur" : "b-past";
  // One figure over the bars: the selected bar's in ink, else the current bar's in brand.
  const figured = (b: SingleBar, i: number) => (sel === null ? !!b.current : i === sel);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={p.ariaLabel}>
      {hits(n, L, slot, H)}
      {p.bars.map((b, i) => (
        <g key={b.key}>
          {b.value != null && b.value > 0 ? (
            <>
              <rect className={barClass(b, i)} x={cx(i) - bw / 2} y={base - h(b.value)} width={bw} height={h(b.value)} rx="2" />
            </>
          ) : null}
          <text className={b.current ? "t-ink" : undefined} x={cx(i)} y={112} textAnchor="middle">
            {b.label}
          </text>
        </g>
      ))}
      {p.avg && !p.rule ? (
        <>
          <line className="g-avg is-muted" x1={L} y1={base - h(p.avg)} x2={W} y2={base - h(p.avg)} />
          <text x={0} y={base - h(p.avg) + 4.5}>
            avg
          </text>
        </>
      ) : null}
      {p.rule ? (
        <>
          <line className="g-avg is-ink" x1={L} y1={base - h(p.rule.value)} x2={W} y2={base - h(p.rule.value)} />
          <text x={0} y={base - h(p.rule.value) + 4.5}>
            {p.rule.label}
          </text>
        </>
      ) : null}
      <line className="g-grid" x1={L} y1={base + 0.5} x2={W} y2={base + 0.5} />
      {/* last, so a later bar or the rule never paints over it */}
      {p.bars.map((b, i) => {
        if (b.value == null || b.value <= 0 || !figured(b, i)) return null;
        const text = show(b.value);
        const at = placed(cx(i), text, W);
        return (
          <text
            key={`fig${b.key}`}
            className={i === sel ? "t-ink t-sel t-halo" : "t-brand t-halo"}
            x={at.x}
            y={base - h(b.value) - 6}
            textAnchor={at.anchor}
          >
            {text}
          </text>
        );
      })}
    </svg>
  );
}

function dense(p: Extract<Props, { mode: "dense" }>, W: number, sel: number | null) {
  const H = 120;
  const base = 92;
  const top = 21;
  const n = p.values.length || 24;
  const slot = W / n;
  const max = Math.max(1, ...p.values.map((v) => v ?? 0));
  const h = (v: number) => Math.max(1.2, (v / max) * (base - top));
  const axis = [0, 6, 12, 18].filter((hr) => hr < n);
  // The figure is wider than its bar; set above the neighbours it spans, or a taller one strikes through it.
  const labelOver = (i: number) =>
    Math.max(...p.values.slice(Math.max(0, i - 2), i + 3).map((v) => (v != null && v > 0 ? h(v) : 0)));
  const figured = (i: number) => (sel === null ? i === p.current : i === sel);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={p.ariaLabel}>
      {hits(n, 0, slot, H)}
      {p.values.map((v, i) =>
        v != null && v > 0 ? (
          <g key={i}>
            <rect
              className={i === sel ? "b-sel" : i === p.current ? "b-cur" : "b-past"}
              x={i * slot + (slot - 10) / 2}
              y={base - h(v)}
              width="10"
              height={h(v)}
              rx="2"
            />
          </g>
        ) : null
      )}
      <line className="g-grid" x1={0} y1={base + 0.5} x2={W} y2={base + 0.5} />
      {axis.map((hr) => (
        <text key={hr} x={hr === 0 ? 1 : hr * slot} y={112} textAnchor={hr === 0 ? "start" : "middle"}>
          {String(hr).padStart(2, "0")}:00
        </text>
      ))}
      {p.values.map((v, i) => {
        if (v == null || v <= 0 || !figured(i)) return null;
        const text = fmt(v);
        const at = placed(i * slot + slot / 2, text, W);
        return (
          <text
            key={`fig${i}`}
            className={i === sel ? "t-ink t-sel t-halo" : "t-brand t-halo"}
            x={at.x}
            y={base - labelOver(i) - 6}
            textAnchor={at.anchor}
          >
            {text}
          </text>
        );
      })}
    </svg>
  );
}
